import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, ArrowRight, ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { getPasswordStrength, getPasswordStrengthLabel, getPasswordStrengthColor } from '../utils/validation';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import '../styles/SignIn.css';

const ResetPasswordConfirm = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { confirmResetPassword, isLoading } = useAuth();
  const { success, error: showError } = useToast();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [localError, setLocalError] = useState('');
  const [done, setDone] = useState(false);

  const handlePasswordChange = (e) => {
    setNewPassword(e.target.value);
    setPasswordStrength(getPasswordStrength(e.target.value));
    if (localError) setLocalError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    // Backend requires min 10 characters + upper + lower + digit.
    if (newPassword.length < 10) {
      setLocalError('Password must be at least 10 characters');
      return;
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      setLocalError('Password must contain an uppercase letter, a lowercase letter, and a digit');
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    try {
      await confirmResetPassword(token, newPassword);
      setDone(true);
      success('✅ Password updated — you can now sign in');
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'This link may be invalid or expired.';
      setLocalError(errorMsg);
      showError(errorMsg);
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  return (
    <>
      <Navbar />
      <motion.div className="signin-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <div className="signin-container" style={{ gridTemplateColumns: '1fr', justifyItems: 'center' }}>
          <motion.div className="signin-form-section" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <motion.form className="signin-form" onSubmit={handleSubmit} style={{ maxWidth: '440px' }}>
              <motion.div variants={itemVariants} initial="hidden" animate="visible" style={{ marginBottom: '1rem' }}>
                <Link to="/signin" className="back-link" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', color: '#6b7280', textDecoration: 'none' }}>
                  <ArrowLeft size={16} />
                  Back to Sign In
                </Link>
              </motion.div>

              {!done ? (
                <>
                  <h2>Set a New Password</h2>
                  <p className="form-subtitle">Enter a new password for your account</p>

                  {localError && (
                    <div className="error-alert">
                      <AlertCircle size={18} />
                      <span>{localError}</span>
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="newPassword">New Password</label>
                    <div className="input-wrapper">
                      <Lock size={18} className="input-icon" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="newPassword"
                        value={newPassword}
                        onChange={handlePasswordChange}
                        placeholder="••••••••"
                        disabled={isLoading}
                        required
                      />
                      <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)} disabled={isLoading}>
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {newPassword && (
                      <div className="password-strength">
                        <div className="strength-bar">
                          <div
                            className="strength-fill"
                            style={{
                              width: `${(passwordStrength / 4) * 100}%`,
                              backgroundColor: getPasswordStrengthColor(passwordStrength),
                            }}
                          />
                        </div>
                        <span className="strength-label">{getPasswordStrengthLabel(passwordStrength)}</span>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm New Password</label>
                    <div className="input-wrapper">
                      <Lock size={18} className="input-icon" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                        disabled={isLoading}
                        required
                      />
                    </div>
                  </div>

                  <motion.button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={isLoading}
                    whileHover={!isLoading ? { scale: 1.02 } : {}}
                    whileTap={!isLoading ? { scale: 0.98 } : {}}
                  >
                    {isLoading ? 'Updating...' : 'Update Password'}
                    {!isLoading && <ArrowRight size={18} />}
                  </motion.button>
                </>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                    <CheckCircle size={48} color="#10b981" />
                  </div>
                  <h2>Password Updated</h2>
                  <p className="form-subtitle">You can now sign in with your new password.</p>
                  <Link to="/signin" className="btn btn-primary btn-lg" style={{ marginTop: '1rem' }}>
                    Go to Sign In
                  </Link>
                </div>
              )}
            </motion.form>
          </motion.div>
        </div>
      </motion.div>
      <Footer />
    </>
  );
};

export default ResetPasswordConfirm;